import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button.tsx';
import { cx, focusables } from './util.ts';
import './Modal.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Acciones al pie («Cancelar», «Aprobar»). */
  footer?: ReactNode;
  /** Elemento que recibe el foco al abrir; por defecto, el primer enfocable. */
  initialFocus?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

interface BaseProps extends ModalProps {
  kind: 'dialog' | 'drawer';
  side?: 'right' | 'left';
}

/** Pila de modales abiertos: solo el de arriba responde a Escape y atrapa el foco. */
const stack: HTMLElement[] = [];

/**
 * Base común de Dialog y Drawer: portal en <body>, foco atrapado con Tab/Mayús+Tab, Escape cierra,
 * el foco vuelve al elemento que lo abrió y el fondo no se desplaza mientras está abierto.
 */
export function ModalBase({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  initialFocus,
  closeLabel = 'Cerrar',
  size = 'md',
  className,
  kind,
  side = 'right',
}: BaseProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stack.push(panel);
    (initialFocus?.current ?? focusables(panel)[0] ?? panel).focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (stack[stack.length - 1] !== panel) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables(panel);
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!first || !last) {
        event.preventDefault();
        panel.focus();
      } else if (!panel.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      stack.splice(stack.indexOf(panel), 1);
      if (previous?.isConnected) previous.focus();
    };
  }, [open, initialFocus]);

  if (!open) return null;

  return createPortal(
    <div className={cx('af-modal', `af-modal--${kind}`, `af-modal--${side}`)}>
      <div className="af-modal__backdrop" aria-hidden="true" onClick={() => onCloseRef.current()} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cx('af-modal__panel', `af-modal__panel--${size}`, className)}
      >
        <header className="af-modal__header">
          <div>
            <h2 id={titleId} className="af-modal__title">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="af-modal__description">
                {description}
              </p>
            )}
          </div>
          <IconButton icon="cerrar" label={closeLabel} onClick={() => onCloseRef.current()} />
        </header>
        <div className="af-modal__body">{children}</div>
        {footer && <footer className="af-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/** Diálogo modal centrado. */
export function Dialog(props: ModalProps) {
  return <ModalBase {...props} kind="dialog" />;
}

export interface DrawerProps extends ModalProps {
  side?: 'right' | 'left';
}

/** Panel lateral modal (detalle de un documento, auditoría). */
export function Drawer(props: DrawerProps) {
  return <ModalBase {...props} kind="drawer" />;
}
